import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LaunchIcon from '@mui/icons-material/Launch';
import PostItem from '../posts/PostItem';
import './WelcomeScreen.css';

const ForcePostDialog = ({ open, onClose, forcePostsData, onMarkPostRead }) => {
  const [forcePosts, setForcePosts] = useState([]);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [readPosts, setReadPosts] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (open) {

      if (forcePostsData && forcePostsData.length > 0) {
        setForcePosts(forcePostsData);
        setCurrentPostIndex(0);
        setReadPosts(new Set());
        setIsLoading(false);
      } else {
        setForcePosts([]);
        setIsLoading(false);
        onClose(); // No force posts, proceed
      }
    }
  }, [open, forcePostsData, onClose]);

  const markPostAsRead = async (postId, postReceiverId) => {
    try {
      // Immediately update local state to remove blue overlay
      setReadPosts(prev => new Set([...prev, postId]));

      // Update the actual post data to reflect the read status
      setForcePosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, read_flag: 'R' }
            : post
        )
      );

      const success = await onMarkPostRead(postReceiverId); // Use postReceiverId instead of postId

      if (!success) {
        // If API call fails, revert the local state
        setReadPosts(prev => {
          const newSet = new Set(prev);

          newSet.delete(postId);

          return newSet;
        });

        setForcePosts(prevPosts =>
          prevPosts.map(post =>
            post.id === postId
              ? { ...post, read_flag: post.read_flag === 'R' ? 'N' : post.read_flag }
              : post
          )
        );
      }
    } catch (error) {
      console.error('Mark post as read error:', error);
      // Revert the local state on error
      setReadPosts(prev => {
        const newSet = new Set(prev);

        newSet.delete(postId);

        return newSet;
      });

      setForcePosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, read_flag: post.read_flag === 'R' ? 'N' : post.read_flag }
            : post
        )
      );
    }
  };

  const handleNext = () => {
    if (currentPostIndex < forcePosts.length - 1) {
      setCurrentPostIndex(currentPostIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPostIndex > 0) {
      setCurrentPostIndex(currentPostIndex - 1);
    }
  };

  const handleMoveToApp = () => {
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (isLoading) {
    return (
      <Dialog open={open} maxWidth={isMobile ? 'xs' : 'md'} fullWidth fullScreen={isMobile}>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <div className="loading-spinner">Loading...</div>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (forcePosts.length === 0) {
    return null;
  }

  const currentPost = forcePosts[currentPostIndex];
  const isCurrentPostRead = readPosts.has(currentPost?.id || '');
  const isLastPost = currentPostIndex === forcePosts.length - 1;
  const isAllPostsRead = forcePosts.every(p => p && (p.read_flag === 'R' || readPosts.has(p.id)));

  return (
    <Dialog
      open={open}
      maxWidth={isMobile ? 'xs' : 'md'}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        className: 'welcome-screen-backdrop',
        style: isMobile ? {
          margin: 0,
          maxHeight: '100%',
          height: '100%',
          overflow: 'hidden',
        } : {
          overflow: 'hidden',
        },
        sx: {
          // Royal glassmorphism effect with golden accents
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 252, 0.96) 100%)',
          backdropFilter: 'blur(30px) saturate(200%)',
          WebkitBackdropFilter: 'blur(30px) saturate(200%)',
          border: '1px solid rgba(255, 255, 255, 0.95)',
          boxShadow: `
        0 40px 80px -20px rgba(0, 0, 0, 0.1),
        0 0 0 1px rgba(255, 255, 255, 0.9),
        inset 0 1px 4px rgba(255, 255, 255, 0.7),
        0 0 20px rgba(212, 175, 55, 0.1)
      `,
          position: 'relative',
          overflow: 'hidden',

          // Royal gradient overlay
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(255, 215, 0, 0.02) 100%)',
            zIndex: 0,
            pointerEvents: 'none',
          },

          // Royal decorative border
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: '2px solid transparent',
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(255, 215, 0, 0.05)) border-box',
            WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
            mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            borderRadius: 'inherit',
            pointerEvents: 'none',
          },
        }
      }}
    >
      <DialogTitle
        sx={{
          padding: isMobile ? 2.5 : 3.5,
          width: '100%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 250, 252, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Close button at the top */}
        <Box sx={{ position: 'absolute', top: isMobile ? 16 : 20, right: isMobile ? 40 : 20 }}>
          <IconButton
            onClick={handleClose}
            disabled={!isAllPostsRead}
            size="small"
            sx={{
              background: isAllPostsRead
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(255, 255, 255, 0.7)',
              color: isAllPostsRead ? '#666' : '#999',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              boxShadow: `
            0 4px 12px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.9),
            inset 0 1px 2px rgba(255, 255, 255, 0.6)
          `,
              width: 32,
              height: 32,
              '&:hover': {
                background: 'rgba(255, 255, 255, 1)',
                color: '#333',
                transform: 'translateY(-1px)',
                boxShadow: `
              0 8px 20px rgba(0, 0, 0, 0.08),
              0 0 0 1px rgba(212, 175, 55, 0.1),
              inset 0 1px 2px rgba(255, 255, 255, 0.8)
            `,
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.5)',
                color: '#ccc',
                border: '1px solid rgba(212, 175, 55, 0.1)',
                transform: 'none',
                boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '& .MuiSvgIcon-root': {
                fontSize: '1rem',
              }
            }}
            title={isAllPostsRead ? 'Close Announcements' : 'Read all announcements to close'}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Main title */}
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="div"
          sx={{
            fontSize: isMobile ? '1.2rem' : '1.5rem',
            fontWeight: 700,
            color: '#1a1a1a',
            letterSpacing: '-0.5px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 4px rgba(255, 255, 255, 0.3)',
            position: 'relative',
            paddingRight: isCurrentPostRead ? 4 : 0,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, #D4AF37, #FFD700, #D4AF37)',
              borderRadius: '2px',
              boxShadow: '0 2px 4px rgba(212, 175, 55, 0.2)',
            }
          }}
        >
          Important Announcement
        </Typography>

        {/* Post counter */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            px: 2.5,
            py: 0.8,
            border: '1px solid rgba(212, 175, 55, 0.15)',
            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.05)',
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#1a1a1a',
              letterSpacing: '0.5px',
            }}
          >
            Announcement
          </Typography>
          <Box
            sx={{
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4AF37, #FFD700)',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: '#D4AF37',
              letterSpacing: '0.5px',
            }}
          >
            {currentPostIndex + 1} of {forcePosts.length}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          flex: 1,
          minHeight: isMobile ? 'calc(100vh - 180px)' : '400px',
        }}
      >
        {/* Royal decorative elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.03) 0%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            width: '100%',
            maxWidth: '600px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? 2 : 3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* Post Item Container */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '500px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 252, 0.99) 100%)',
              backdropFilter: 'blur(25px)',
              borderRadius: '24px',
              border: '2px solid rgba(255, 255, 255, 1)',
              boxShadow: `
            0 25px 50px -12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(255, 255, 255, 0.95),
            inset 0 1px 4px rgba(255, 255, 255, 0.8),
            0 0 40px rgba(212, 175, 55, 0.05)
          `,
              p: isMobile ? 3 : 4,
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 5,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.03) 0%, rgba(255, 215, 0, 0.02) 100%)',
                zIndex: 0,
                pointerEvents: 'none',
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '1px solid transparent',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(255, 215, 0, 0.05)) border-box',
                WebkitMask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                mask: 'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                borderRadius: '22px',
                pointerEvents: 'none',
              },
            }}
          >
            {/* Navigation buttons positioned INSIDE the PostItem container */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 24,
                transform: 'translateY(-50%)',
                zIndex: 20,
              }}
            >
              <IconButton
                onClick={handlePrevious}
                disabled={currentPostIndex === 0}
                size="small"
                sx={{
                  background: currentPostIndex === 0
                    ? 'rgba(255, 255, 255, 0.85)'
                    : 'rgba(255, 255, 255, 0.95)',
                  color: currentPostIndex === 0 ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(212, 175, 55, 0.25)',
                  boxShadow: `
                0 4px 12px rgba(0, 0, 0, 0.08),
                0 0 0 1px rgba(255, 255, 255, 0.95),
                inset 0 1px 2px rgba(255, 255, 255, 0.8)
              `,
                  width: 36,
                  height: 36,
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 1)',
                    color: currentPostIndex === 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(25, 118, 210, 0.9)',
                    transform: 'translateY(-1px) scale(1.05)',
                    boxShadow: `
                  0 8px 20px rgba(0, 0, 0, 0.1),
                  0 0 0 1px rgba(212, 175, 55, 0.2),
                  inset 0 1px 3px rgba(255, 255, 255, 0.9)
                `,
                    '& .MuiSvgIcon-root': {
                      color: currentPostIndex === 0 ? 'rgba(0, 0, 0, 0.5)' : 'rgba(25, 118, 210, 1)',
                    }
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.6)',
                    color: 'rgba(0, 0, 0, 0.25)',
                    border: '1px solid rgba(212, 175, 55, 0.15)',
                    transform: 'none',
                    boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.4)',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1rem',
                    transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
            </Box>

            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: 24,
                transform: 'translateY(-50%)',
                zIndex: 20,
              }}
            >
              {!isLastPost ? (
                <IconButton
                  onClick={handleNext}
                  disabled={!isCurrentPostRead}
                  size="small"
                  sx={{
                    background: isCurrentPostRead
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'rgba(255, 255, 255, 0.85)',
                    color: isCurrentPostRead ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(212, 175, 55, 0.25)',
                    boxShadow: `
                  0 4px 12px rgba(0, 0, 0, 0.08),
                  0 0 0 1px rgba(255, 255, 255, 0.95),
                  inset 0 1px 2px rgba(255, 255, 255, 0.8)
                `,
                    width: 36,
                    height: 36,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 1)',
                      color: isCurrentPostRead ? 'rgba(25, 118, 210, 0.9)' : 'rgba(0, 0, 0, 0.5)',
                      transform: 'translateY(-1px) scale(1.05)',
                      boxShadow: `
                    0 8px 20px rgba(0, 0, 0, 0.1),
                    0 0 0 1px rgba(212, 175, 55, 0.2),
                    inset 0 1px 3px rgba(255, 255, 255, 0.9)
                  `,
                      '& .MuiSvgIcon-root': {
                        color: isCurrentPostRead ? 'rgba(25, 118, 210, 1)' : 'rgba(0, 0, 0, 0.5)',
                      }
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.6)',
                      color: 'rgba(0, 0, 0, 0.25)',
                      border: '1px solid rgba(212, 175, 55, 0.15)',
                      transform: 'none',
                      boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.4)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem',
                      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                  }}
                  title={isCurrentPostRead ? 'Next Post' : 'Read current post to continue'}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              ) : (
                <IconButton
                  onClick={handleMoveToApp}
                  disabled={!isAllPostsRead}
                  size="small"
                  sx={{
                    background: isAllPostsRead
                      ? 'rgba(255, 255, 255, 0.95)'
                      : 'rgba(255, 255, 255, 0.85)',
                    color: isAllPostsRead ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(212, 175, 55, 0.25)',
                    boxShadow: `
                  0 4px 12px rgba(0, 0, 0, 0.08),
                  0 0 0 1px rgba(255, 255, 255, 0.95),
                  inset 0 1px 2px rgba(255, 255, 255, 0.8)
                `,
                    width: 36,
                    height: 36,
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 1)',
                      color: isAllPostsRead ? 'rgba(46, 125, 50, 0.9)' : 'rgba(0, 0, 0, 0.5)',
                      transform: 'translateY(-1px) scale(1.05)',
                      boxShadow: `
                    0 8px 20px rgba(0, 0, 0, 0.1),
                    0 0 0 1px rgba(212, 175, 55, 0.2),
                    inset 0 1px 3px rgba(255, 255, 255, 0.9)
                  `,
                      '& .MuiSvgIcon-root': {
                        color: isAllPostsRead ? 'rgba(46, 125, 50, 1)' : 'rgba(0, 0, 0, 0.5)',
                      }
                    },
                    '&:disabled': {
                      background: 'rgba(255, 255, 255, 0.6)',
                      color: 'rgba(0, 0, 0, 0.25)',
                      border: '1px solid rgba(212, 175, 55, 0.15)',
                      transform: 'none',
                      boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.4)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiSvgIcon-root': {
                      fontSize: '1rem',
                      transition: 'color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                  }}
                  title={isAllPostsRead ? 'Move to App' : 'Read all posts to continue'}
                >
                  <LaunchIcon />
                </IconButton>
              )}
            </Box>

            {/* Royal corner accents */}
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                left: -2,
                width: '40px',
                height: '40px',
                borderTop: '2px solid rgba(212, 175, 55, 0.3)',
                borderLeft: '2px solid rgba(212, 175, 55, 0.3)',
                borderTopLeftRadius: '24px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: '40px',
                height: '40px',
                borderTop: '2px solid rgba(212, 175, 55, 0.3)',
                borderRight: '2px solid rgba(212, 175, 55, 0.3)',
                borderTopRightRadius: '24px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                left: -2,
                width: '40px',
                height: '40px',
                borderBottom: '2px solid rgba(212, 175, 55, 0.3)',
                borderLeft: '2px solid rgba(212, 175, 55, 0.3)',
                borderBottomLeftRadius: '24px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: '40px',
                height: '40px',
                borderBottom: '2px solid rgba(212, 175, 55, 0.3)',
                borderRight: '2px solid rgba(212, 175, 55, 0.3)',
                borderBottomRightRadius: '24px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />

            <Box
              position="relative"
              sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                zIndex: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(212, 175, 55, 0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.3), rgba(255, 215, 0, 0.2))',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.4), rgba(255, 215, 0, 0.3))',
                  }
                },
              }}
            >
              <PostItem
                data={{
                  ...currentPost,
                  read_flag: isCurrentPostRead ? 'R' : currentPost.read_flag
                }}
                showbutton={false}
                showshare={false}
                showButtonT={false}
                callfrom="force"
                addRead={(postReceiverId) => {
                  if (postReceiverId && !isCurrentPostRead) {
                    markPostAsRead(currentPost.id, postReceiverId);
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePostDialog;